import type { IsoTimestamp } from '~/types/dates'
import { newId, type SentinelLiftId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssues } from './common'
import { DB_SCHEMA_VERSION } from './schemaVersion'

/**
 * A sentinel lift: one exercise the user tracks to see whether strength is
 * holding up (data-model.md).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  `active` IS A STRING, AND THAT IS NOT A STYLE CHOICE.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The v1 schema declares an index on `active` (D-027). **IndexedDB has no
 * boolean key type** — valid keys are numbers, strings, Dates, binary and arrays
 * of those. A record whose indexed property holds `true` is not rejected; it is
 * silently *omitted from the index*, so `getAllFromIndex('sentinelLifts',
 * 'active', true)` would return an empty list forever and the lift list would
 * simply look empty.
 *
 * Storing the status as a string keeps the declared index usable without
 * touching the schema. `sentinelLiftsIndexIsQueryable` in the repository tests
 * pins this: it was verified to fail against a boolean-valued `active`.
 *
 * Suggested lifts (presses, squat or leg press, Romanian deadlift, a vertical
 * pull, a row, overhead press) are examples in the handoff, not defaults the app
 * creates. Nothing here seeds a list — the user names their own.
 */
export const SENTINEL_LIFT_STATUSES = ['active', 'retired'] as const

export type SentinelLiftStatus = (typeof SENTINEL_LIFT_STATUSES)[number]

export interface SentinelLiftRecord extends RecordEnvelope {
  readonly id: SentinelLiftId
  readonly name: string
  /** Indexed. A string, for the reason above. */
  readonly active: SentinelLiftStatus
  /** Indexed. Display order in the strength step, ascending. */
  readonly order: number
  readonly category?: string
  readonly notes?: string
}

export interface SentinelLiftInput {
  readonly name: string
  readonly order: number
  readonly category?: string
  readonly notes?: string
}

const MAX_NAME_LENGTH = 80

export function isSentinelLiftStatus(value: string): value is SentinelLiftStatus {
  return (SENTINEL_LIFT_STATUSES as readonly string[]).includes(value)
}

export function validateSentinelLift(input: SentinelLiftInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  const name = input.name.trim()
  if (name.length === 0) {
    issues.push(issue('name', 'Give the lift a name.'))
  } else if (name.length > MAX_NAME_LENGTH) {
    issues.push(issue('name', `Keep the name under ${MAX_NAME_LENGTH} characters.`))
  }

  if (!Number.isInteger(input.order) || input.order < 0) {
    issues.push(issue('order', 'Order must be a whole number of zero or more.'))
  }

  return issues
}

type ValidationIssue = ValidationIssues[number]

/** Identity carried across an edit, so renaming a lift does not mint a new one. */
export interface ExistingSentinelLiftIdentity {
  readonly id: SentinelLiftId
  readonly active: SentinelLiftStatus
  readonly createdAt: IsoTimestamp
}

export function buildSentinelLiftRecord(
  input: SentinelLiftInput,
  now: IsoTimestamp,
  existing?: ExistingSentinelLiftIdentity,
): Result<SentinelLiftRecord, ValidationIssues> {
  const issues = validateSentinelLift(input)
  if (issues.length > 0) return err(issues)

  return ok({
    id: existing?.id ?? newId<'SentinelLiftId'>(),
    name: input.name.trim(),
    active: existing?.active ?? 'active',
    order: input.order,
    ...(input.category === undefined ? {} : { category: input.category }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}
